import { useQuery } from "@apollo/client/react";
import { UsageTrackerEvent, usageTracker } from "@volumental/analytic";
import { ButtonGridView, Headline, Loader } from "@volumental/common";
import type { LabeledValue } from "@volumental/common";
import { useState } from "react";
import type { JSX } from "react";
import { useTranslation } from "react-i18next";
import { WidthLabelQuery } from "../../../api/queries/survey-query";
import type { WidthStep } from "../../../store/survey";
import { findWithFallback } from "./find-value";
import { selectTimeoutMs } from "./constants";

interface SizePickerProps {
  survey: WidthStep;
  onClose: (width: LabeledValue<number | undefined>) => void;
}
export function WidthSizeSelector({
  survey: { brand, gender, length, locale, width },
  onClose,
}: SizePickerProps): JSX.Element {
  const { t } = useTranslation();
  const [widthSize, setWidthSize] = useState<number | undefined>();

  const { data, loading } = useQuery(WidthLabelQuery, {
    variables: {
      brandName: brand.value,
      genderAge: gender.value,
      length: length.value,
      sizeLocale: locale,
    },
  });

  const defaultValue = {
    label: t("noWidth"),
    value: undefined,
  };

  const onReady = (selected: number | undefined): void => {
    setWidthSize(selected);

    if (width !== selected) {
      usageTracker.track(UsageTrackerEvent.AddWidthToSurvey, {
        size: length,
        locale,
        width: selected,
      });
    }
    const selectedWidth = findWithFallback(selected, [defaultValue, ...widthList]);

    setTimeout(() => {
      onClose(selectedWidth);
    }, selectTimeoutMs);
  };
  const widthExplanationFromOffset = (offsetFromDefaultWidth: number): string => {
    if (offsetFromDefaultWidth < -4) {
      return t("widthLabel.extraExtraNarrow");
    }
    if (offsetFromDefaultWidth < -2) {
      return t("widthLabel.extraNarrow");
    }
    if (offsetFromDefaultWidth < 0) {
      return t("widthLabel.narrow");
    }
    if (offsetFromDefaultWidth === 0) {
      return t("widthLabel.medium");
    }
    if (offsetFromDefaultWidth <= 2) {
      return t("widthLabel.wide");
    }
    if (offsetFromDefaultWidth <= 4) {
      return t("widthLabel.extraWide");
    }
    if (offsetFromDefaultWidth > 4) {
      return t("widthLabel.extraExtraWide");
    }
    return "";
  };
  if (loading) {
    return <Loader />;
  }

  const widthList =
    data?.recommendableWidthLabels?.map((item) => ({
      label: `${widthExplanationFromOffset(item.offsetFromDefaultWidth)} (${item.label})`,
      value: item.value,
    })) ?? [];
  return (
    <>
      <Headline className="text-xl">{t("shoeWidth")}</Headline>
      <ButtonGridView
        singleColumnMode
        options={[defaultValue, ...widthList]}
        onSelect={onReady}
        selected={widthSize}
        groupAriaLabel={t("ariaLabels.widthSize")}
      />
    </>
  );
}
